import { Link } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { getWeatherForClub, isRainy } from "../services/weatherService";
import { apiGet } from "../services/api";
import { isLogged } from "../services/auth";
import WeatherCourt3D from "../components/WeatherCourt3D";
import "./home.css";
import "./inicio.css";

const CLUB_LAT = 39.483017;
const CLUB_LON = -6.364445;

const ACTIONS = [
  { label: "Reservar pista", to: "/reservas", primary: true },
  { label: "Clases", to: "/clases" },
  { label: "Torneos", to: "/torneos" },
];

const SCENES = [
  { id: "ahora", label: "Ahora" },
  { id: "dia", label: "Día", night: false },
  { id: "noche", label: "Noche", night: true },
];

const NEWS_ITEMS = [
  {
    id: 1,
    category: "Liga",
    date: "26 abr 2026",
    title: "La liga femenina brilla en casa",
    summary:
      "El equipo compitió con buen ritmo y el ambiente del club se notó desde el primer partido.",
    image: `${process.env.PUBLIC_URL}/fotosLiga/ligafem.jpeg`,
  },
  {
    id: 2,
    category: "Clases",
    date: "24 abr 2026",
    title: "Sesión técnica para mejorar bandeja y red",
    summary:
      "Los grupos trabajan situaciones reales de partido para ganar confianza y ordenar mejor el juego.",
    image: `${process.env.PUBLIC_URL}/fotosClase/clases.jpeg`,
  },
  {
    id: 3,
    category: "Club",
    date: "22 abr 2026",
    title: "Premios y buen ambiente tras el torneo del club",
    summary:
      "Alumnos y familias compartieron pista, premios y una jornada de esas que hacen club.",
    image: `${process.env.PUBLIC_URL}/fotosAlumnos/premiodanipau.jpeg`,
  },
  {
    id: 4,
    category: "Alumnos",
    date: "19 abr 2026",
    title: "Más partidos entre alumnos durante la semana",
    summary:
      "La escuela sigue creciendo con reservas, clases y partidos organizados entre alumnos.",
    image: `${process.env.PUBLIC_URL}/fotosAlumnos/nani.jpeg`,
  },
];

function formatDay(dateStr) {
  const date = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return "Hoy";
  if (date.toDateString() === tomorrow.toDateString()) return "Mañana";

  return date.toLocaleDateString("es-ES", { weekday: "short", day: "2-digit" });
}

function formatHour(isoTime) {
  return new Date(isoTime).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

function Home() {
  const loggedIn = isLogged();
  const [weather, setWeather] = useState(null);
  const [weatherError, setWeatherError] = useState("");
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  const [sceneId, setSceneId] = useState("ahora");
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [activeAlertsError, setActiveAlertsError] = useState("");

  const loadWeather = useCallback(async () => {
    try {
      setWeatherError("");
      const data = await getWeatherForClub(CLUB_LAT, CLUB_LON);
      if (!data.ok) throw new Error("No hemos podido consultar el tiempo.");
      setWeather(data);
    } catch (err) {
      setWeatherError(err.message || "No hemos podido consultar el tiempo.");
    }
  }, []);

  useEffect(() => {
    loadWeather();
  }, [loadWeather]);

  useEffect(() => {
    if (!loggedIn) return;
    apiGet("/api/notificaciones?limit=3&active=1&important=1")
      .then((data) => {
        setActiveAlerts(data.notifications || []);
        setActiveAlertsError("");
      })
      .catch(() => {
        setActiveAlerts([]);
        setActiveAlertsError("No se pudieron cargar las notificaciones. Inténtalo de nuevo más tarde.");
      });
  }, [loggedIn]);

  const scene = SCENES.find((item) => item.id === sceneId) || SCENES[0];
  const sceneIndex = SCENES.indexOf(scene);
  const previewing = scene.id !== "ahora";
  const night = scene.night ?? Number(weather?.isDay) === 0;
  const rain = scene.rain ?? (weather ? isRainy(weather) : false);
  const playable = weather ? weather.playability.playable : null;
  const verdictClass = playable === null ? "isPending" : playable ? "isPlayable" : "isBlocked";
  const today = new Date().toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  let headline = "Consultando el tiempo…";
  if (weatherError) headline = "No hemos podido ver el tiempo";
  else if (playable === true) headline = "Hoy se puede jugar";
  else if (playable === false) headline = "Hoy mejor no jugar";

  return (
    <main className="inicio" aria-labelledby="home-title">
      <section className={`inicioHero${night ? " isNight" : ""}${rain ? " isRaining" : ""}`}>
        <WeatherCourt3D weather={weather} night={night} rain={rain} />

        <div className="inicioInner">
          <div className="inicioInfo">
            <span className="inicioEyebrow">Escuela NaniPadel · {today}</span>

            <h1 id="home-title" className={`inicioVerdict ${verdictClass}`}>
              <span className="inicioVerdictDot" aria-hidden="true" />
              {headline}
            </h1>

            <p className="inicioReason" aria-live="polite">
              {weatherError
                ? "Puedes reservar igualmente y comprobarlo más tarde."
                : weather
                  ? `${weather.playability.reason}. ${weather.description}, ${Math.round(weather.temperature)}°C.`
                  : "Un momento, estamos mirando cómo está la pista."}
            </p>

            {weatherError && (
              <button type="button" className="inicioRetry" onClick={loadWeather}>
                Reintentar
              </button>
            )}

            {weather && (
              <dl className="inicioStats">
                <div>
                  <dt>Lluvia</dt>
                  <dd>{weather.precipitationProbability}%</dd>
                </div>
                <div>
                  <dt>Viento</dt>
                  <dd>{Math.round(weather.windSpeed)} km/h</dd>
                </div>
                <div>
                  <dt>Humedad</dt>
                  <dd>{weather.humidity}%</dd>
                </div>
              </dl>
            )}

            <nav className="inicioActions" aria-label="Acciones principales">
              {ACTIONS.map((action) => (
                <Link
                  key={action.to}
                  to={action.to}
                  className={`inicioAction${action.primary ? " isPrimary" : ""}`}
                >
                  {action.label}
                </Link>
              ))}
            </nav>

            {weather && weather.hourly.length > 0 && (
              <div className="inicioHours">
                <div className="inicioHoursHead">
                  <span>Próximas horas</span>
                  <button type="button" onClick={() => setShowWeatherModal(true)}>
                    Previsión 7 días
                  </button>
                </div>
                <ol className="inicioHoursList">
                  {weather.hourly.slice(0, 8).map((hour, index) => (
                    <li
                      key={hour.time}
                      className={hour.playable ? "isPlayable" : "isBlocked"}
                      style={{ "--i": index }}
                    >
                      <span>{index === 0 ? "Ahora" : formatHour(hour.time)}</span>
                      <i aria-hidden="true" />
                      <strong>{Math.round(hour.temperature)}°</strong>
                      <small>{hour.playable ? "Se juega" : "No"}</small>
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <div className="inicioPreview">
              <span id="inicio-preview-label">Ver la pista de</span>
              <div className="inicioTabs" role="group" aria-labelledby="inicio-preview-label" style={{ "--i": sceneIndex }}>
                {SCENES.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={item.id === sceneId}
                    onClick={() => setSceneId(item.id)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <aside className="inicioWeather" aria-label="Condiciones de la pista">
            {weather && (
              <div className="inicioWeatherNow">
                <strong>{Math.round(weather.temperature)}°</strong>
                <span>{previewing ? `Vista previa: ${scene.label.toLowerCase()}` : weather.description}</span>
              </div>
            )}
          </aside>

        </div>
      </section>

      {showWeatherModal && weather && (
        <div className="weatherModalBackdrop" onClick={() => setShowWeatherModal(false)}>
          <div className="weatherModal" onClick={(event) => event.stopPropagation()}>
            <div className="weatherModalHead">
              <h2>Tiempo en Norba Padel</h2>
              <button
                className="weatherCloseBtn"
                onClick={() => setShowWeatherModal(false)}
                aria-label="Cerrar detalle del tiempo"
              >
                ×
              </button>
            </div>

            <div className="weatherCurrentBig">
              <div className="weatherCurrentIcon">{weather.emoji}</div>
              <div className="weatherCurrentInfo">
                <p className="weatherCurrentTemp">{Math.round(weather.temperature)}°C</p>
                <p className="weatherCurrentDesc">{weather.description}</p>
                <p className="weatherCurrentFeel">
                  Sensación térmica: {Math.round(weather.apparentTemperature)}°C
                </p>
              </div>
            </div>

            <div className="weatherDetailGrid">
              <div className="weatherDetailItem">
                <span className="wdLabel">Humedad</span>
                <span className="wdValue">{weather.humidity}%</span>
              </div>
              <div className="weatherDetailItem">
                <span className="wdLabel">Viento</span>
                <span className="wdValue">{weather.windSpeed} km/h</span>
              </div>
              <div className="weatherDetailItem">
                <span className="wdLabel">Lluvia</span>
                <span className="wdValue">{weather.precipitationProbability}%</span>
              </div>
              <div className="weatherDetailItem">
                <span className="wdLabel">Indice UV</span>
                <span className="wdValue">{weather.uvIndex ?? "-"}</span>
              </div>
            </div>

            {weather.recomendacion && (
              <div className={`recomendacionBox rec-${weather.recomendacion.nivel}`}>
                <div className="recIcon">{weather.recomendacion.icono}</div>
                <div className="recText">
                  <strong>{weather.recomendacion.titulo}</strong>
                  <p>{weather.recomendacion.mensaje}</p>
                </div>
              </div>
            )}

            {weather.forecast && weather.forecast.length > 0 && (
              <div className="forecastSection">
                <h3>Previsión de los próximos 7 días</h3>
                <div className="forecastList">
                  {weather.forecast.map((day) => (
                    <div className="forecastDay" key={day.date}>
                      <span className="fDayName">{formatDay(day.date)}</span>
                      <span className="fDayIcon">{day.emoji}</span>
                      <span className="fDayDesc">{day.description}</span>
                      <span className="fDayTemps">
                        <strong>{Math.round(day.tempMax)}°</strong>
                        <span className="fDayMin"> / {Math.round(day.tempMin)}°</span>
                      </span>
                      <span className="fDayRain">{day.precipitationProbability}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="weatherUpdated">
              Actualizado: {new Date(weather.updatedAt).toLocaleString()}
            </p>
            <Link className="weatherModalPageLink" to="/estado-pista">
              Abrir pagina completa
            </Link>
          </div>
        </div>
      )}

      {loggedIn && (
        <section className="homeAlerts">
          <div className="sectionHeader sectionHeaderInline">
            <div>
              <span className="sectionEyebrow">Avisos activos</span>
              <h2>Lo importante del club</h2>
            </div>
            <Link className="newsMoreBtn" to="/avisos">
              Ver todos
            </Link>
          </div>

          {activeAlertsError ? (
            <p className="homeAlertsEmpty">{activeAlertsError}</p>
          ) : activeAlerts.length === 0 ? (
            <p className="homeAlertsEmpty">No tienes avisos pendientes.</p>
          ) : (
            <div className="homeAlertsGrid">
              {activeAlerts.map((item) => (
                <Link className={`homeAlertCard priority-${item.priority || "normal"}`} to="/avisos" key={item.id}>
                  <span>{item.category || item.tipo}</span>
                  <strong>{item.title}</strong>
                  <small>{item.body}</small>
                </Link>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="inicioNews" aria-labelledby="inicio-news-title">
        <div className="sectionHeader sectionHeaderInline">
          <div>
            <span className="sectionEyebrow">Club en marcha</span>
            <h2 id="inicio-news-title">Noticias del club</h2>
          </div>
          <Link className="newsMoreBtn" to="/galeria">
            Ver todas
          </Link>
        </div>

        <div className="inicioNewsGrid">
          {NEWS_ITEMS.slice(0, 3).map((item, index) => (
            <Link className="inicioNewsCard" to="/galeria" key={item.id} style={{ "--i": index }}>
              <img src={item.image} alt="" loading="lazy" />
              <div>
                <span>
                  {item.category} · {item.date}
                </span>
                <h3>{item.title}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Home;
